/* eslint-disable react-hooks/exhaustive-deps */

import { Box, ThemeProvider, createTheme } from '@mui/material';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core';
import FullscreenSpinner from 'modules/common/fullscreenSpinner.component';
import { Permissions } from 'modules/layout/permissions';
import { hasPermission } from 'modules/layout/permissionService';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import {
  getAuthClientPolicies,
  getAuthDateSummary,
  getAuthorizationSummary,
  getCommunicationInfo,
  getDepartureAddress,
  getDestinationAddress,
  getEmployeeInfo,
  getFamilyMembers,
  getFamilyPets,
  getServices,
  getTeam,
  getUserSettings,
  setAuthId,
  setLoadingComplete,
  getAddress,
  getAuthTripsByAuthId
} from '../../store/actions/authorizations.actions';

import { getBrokerAgents, getVendorContacts, getVendors } from 'modules/authorizations/store/actions/dataManagement.actions';

import {
  getAssignmentExtensions,
  getAssignmentTrips,
  getGlobalDestinationServices,
  getGlobalInfo,
  getGlobalInternationalSchooling,
  getGlobalLanguageTraining,
  getGlobalAssignment,
  getGlobalPropertyManagementInfo
} from 'modules/authorizations/store/actions/global.actions';

import { COLOR_PRIMARY, COLOR_SECONDARY, COLOR_WARNING } from 'styles/theme';

import GlobalService from './globalService.component';
import {
  fetchAuthDetails,
  fetchAuthGlobalDetails,
  fetchEnumerations,
  fetchVendorContacts,
  fetchVendors
} from 'modules/authorizations/utils/fetchDataDetailsService';
import { showToast } from 'modules/layout/layout.actions';
import {
  getAllCountries,
  getAllCurrencies,
  getAllEnumerations,
  getAllProvinces,
  getAllSaleTypes,
  getAllWeightTypes
} from 'modules/enumerations/store/enumerationActions';
import { getAgents } from 'modules/common/common.actions';
import { getEmailTemplateSummaries } from 'modules/emailTemplates/store/emailTemplate.actions';
import { getClientDetails, setClientId } from 'modules/clients/clients.actions';

const styles = theme => ({
  card: {
    width: '100%',
    maxWidth: 1500,
    flexGrow: 1,
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  cardContainer: {
    maxWidth: '100% !important',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  cardDisplayContent: {
    maxWidth: '100% !important',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    margin: '10px'
  }
});
const SHORT_TERM_ASSIGNMENT_MOVE_TYPE_ID = '83BBE794-E171-403E-B545-23508B017354';


const GlobalView = props => {
  const theme = createTheme({
    palette: {
      primary: COLOR_PRIMARY,
      secondary: COLOR_SECONDARY,
      warning: COLOR_WARNING
    }
  });

  const details = props.authorizationDetails ? props.authorizationDetails[props.match.params.id] : null;

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    async function fetchData() {
      const id = props.match.params.id;

      window.scrollTo(0, 0);
      props.setAuthId(id);
      setIsLoading(true);

      await fetchAuthDetails(details, id, props);
      await fetchVendors(props?.vendors, props);
      await fetchVendorContacts(props?.vendorContacts, props);
      await fetchAuthGlobalDetails(details, id, props);
      await fetchEnumerations(props);
      if (!props.agents || props.agents.length === 0) {
        await props.getAgents();
      }
      if (!props.templates || props.templates.isLoaded === false) {
        await props.getEmailTemplateSummaries();
      }
      props.setLoadingComplete();
      setIsLoading(false);
    }

    fetchData();
  }, []);
  
  const canReadGlobal = hasPermission(Permissions.File.Global.Read);
  const canEditGlobal = hasPermission(Permissions.File.Global.Edit);
  const isShortTermAssignment = details?.authorizationDetails?.moveTypeId?.toUpperCase() === SHORT_TERM_ASSIGNMENT_MOVE_TYPE_ID;
  const staEditAccess = hasPermission(Permissions.File.STA.Edit);

  if (!canReadGlobal) {
    props.history.push('/');
    return null;
  }

  return (
    <ThemeProvider theme={theme}>
      <Box>
        {!isLoading && details && (
          <GlobalService
            details={details}
            clientInfo={props.clientsInfo[details.authorizationDetails.clientId]}
            id={props.match.params.id}
            history={props.history}
            location={props.location}
            enumerations={props.enumerations.allEnums}
            vendors={props.vendors}
            vendorContacts={props.vendorContacts}
            brokerAgents={props.agents}
            emailTemplates={props.templates ? props.templates.emailTemplates : []}
            isReadOnly={isShortTermAssignment ? !staEditAccess : !canEditGlobal}
          />
        )}
        {isLoading && <FullscreenSpinner />}
      </Box>
    </ThemeProvider>
  );
};

GlobalView.propTypes = {
  classes: PropTypes.object.isRequired
};

const mapStateToProps = state => {
  const { authorizationDetails, isLoading } = state.authorizations;
  const { unmappedClients, clientsInfo } = state.clients;
  const enumerations = state.enumerations;
  const { vendors, vendorContacts } = state.authorizations;
  const { agents } = state.common;
  const templates = state.templates;

  return {
    authorizationDetails,
    unmappedClients,
    isLoading,
    vendors,
    enumerations,
    agents,
    templates,
    clientsInfo,
    vendorContacts
  };
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, {
    getAuthorizationSummary,
    getEmployeeInfo,
    getCommunicationInfo,
    getAuthClientPolicies,
    setAuthId,
    setLoadingComplete,
    getAuthDateSummary,
    getDepartureAddress,
    getDestinationAddress,
    getTeam,
    getServices,
    getUserSettings,
    getGlobalInfo,
    getAssignmentTrips,
    getAssignmentExtensions,
    getVendorContacts,
    getVendors,
    getBrokerAgents,
    getFamilyMembers,
    getFamilyPets,
    showToast,
    getGlobalInternationalSchooling,
    getGlobalDestinationServices,
    getGlobalLanguageTraining,
    getAddress,
    getAuthTripsByAuthId,
    getAllEnumerations,
    getAllSaleTypes,
    getAllWeightTypes,
    getAllProvinces,
    getAllCurrencies,
    getAllCountries,
    getGlobalAssignment,
    getGlobalPropertyManagementInfo,
    getAgents,
    getEmailTemplateSummaries,
    getClientDetails,
    setClientId
  })
)(GlobalView);
